<template>
  <div class="w-full h-full relative container-1">

    <div class="space-y-20">
      <h1 class="animate-title-service text-ellie-blue whitespace-pre-line font-semibold uppercase text-center text-6xl md:text-[4.167vw] big-screen:text-[6vw] leading-[1]">
        {{ "Our Services" }}
      </h1>
      
      <Swiper
        ref="serviceContainer"
        :modules="[SwiperScrollbar]"
        :navigation="{
          nextEl: '.swiper-next',
          prevEl: '.swiper-prev',
        }"
        :breakpoints="{
          200: {
              slidesPerView: 1.1,
              spaceBetween: 24
          },
          600: {
              slidesPerView: 1.5,
              spaceBetween: 24
          },
          800: {
              slidesPerView: 3,
              spaceBetween: 24
          },
          1280: {
              slidesPerView: 3,
              spaceBetween: 24
          }
        }"
        class="swiper-service"
        @swiper="onSwiper"
        :scrollbar="{
          el: '.scrollbar-services',
          hide: false,
        }"
        @mouseenter="toggleHover(true , 'drag')" @mouseleave="toggleHover(false, 'default')"
      >
        <SwiperSlide v-for="(service, i) in services" :key="service.id" @click="service.show = !service.show" class="group relative flex items-start border border-black border-opacity-10 bg-ellie-yellow bg-opacity-30 backdrop-blur-sm overflow-clip max-w-[86vw] lg:max-w-[80vw] rounded-xl lg:rounded-3xl">
          <div class="w-full h-full relative">
            <div class="absolute w-full h-full top-0 left-0 bg-[#D9D9D9] bg-opacity-10"/>
            <div class="relative h-full w-full flex flex-col justify-between gap-10 md:gap-2 items-start py-6 px-5 lg:py-12 lg:px-[2vw] xl:py-[3vw] xl:px-[4vw] z-2">
              <h2 class="text-2xl md:text-3xl xl:text-[2vw] whitespace-pre-line !leading-[1.4] animate-content">{{ service.title }}</h2>
              <figure class="animate-content flex-shrink-0 w-full">
                <div class="icon w-full h-full flex items-center justify-center">
                  <LottieServices0 v-if="i == 0" :src="services[0].lottie" />
                  <LottieServices1 v-if="i == 1" :src="services[1].lottie" />
                  <LottieServices2 v-if="i == 2" :src="services[2].lottie" />
                  <LottieServices3 v-if="i == 3" :src="services[3].lottie" />
                  <LottieServices4 v-if="i == 4" :src="services[4].lottie" />
                </div>
              </figure>
            </div>
          </div>
          <div v-if="isL" class="w-full h-full absolute bg-ellie-yellow top-0 left-0 translate-y-full group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-700">
            <div class="origin-center scale-75 group-hover:scale-100 w-full h-full py-6 px-5 lg:py-12 lg:px-[2vw] xl:py-[3vw] xl:px-[4vw] flex justify-between flex-col transition-all duration-700">
              <h2 class="text-2xl md:text-3xl xl:text-[2vw] whitespace-pre-line !leading-[1.4] animate-content">{{ service.title }}</h2>
              <div class="space-y-5 text-base xl:text-[1.1vw] leading-[1.4] animate-content" v-html="service.desc"/>
            </div>
          </div>
          <TransitionSlide :offset="[0, 100]">
            <div v-if="!isL && service.show" class="w-full h-full absolute bg-ellie-yellow top-0 left-0 transition-all duration-700">
              <div :class="service.show ? 'scale-100':'scale-75'" class="origin-center w-full h-full py-6 px-5 lg:py-12 lg:px-[2vw] xl:py-[3vw] xl:px-[4vw] flex justify-between flex-col transition-all duration-700">
                <h2 class="text-2xl md:text-3xl xl:text-[2vw] whitespace-pre-line !leading-[1.4] animate-content">{{ service.title }}</h2>
                <div class="space-y-5 text-base xl:text-[1.1vw] leading-[1.4] animate-content" v-html="service.desc"/>
              </div>
            </div>
          </TransitionSlide>
        </SwiperSlide>
      </Swiper>
      <div v-if="!isL" class="max-w-48 mx-auto mt-12">
        <div class="scrollbar-services"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
const { $gsap, $anime } = useNuxtApp();
const isM = useMediaQuery('(min-width: 768px)');
const isL = useMediaQuery('(min-width: 1024px)');

const toggleShow = ref(false)

const mainStore = useMainStore();
const toggleHover = (isHover, type = 'default') => {
  const payload = { isHover, type };
  mainStore.toggleTrackingMouse(payload);
};

const services = ref([
  {
    id: 0,
    title: '2D and 3D Animation, \nMotion Design',
    desc: `
      <p>We specialize in creating brand mascots, expressive texts, and dynamic motion designs. By collaborating with experienced designers, we deliver versatile solutions for both small and large-scale projects, ensuring adaptability to evolving marketing strategies.</p>
    `,
    icon: '/icons/animation.svg',
    pattern: 'animation',
    lottie: 'https://lottie.host/b3229210-bf13-4a5a-b5e1-ffad7449502d/Qx1oNmXvpF.lottie',
    show: false,
  },
  {
    id: 1,
    title: 'Brand Identity Design',
    desc: `
      <p>We craft distinct brand identities that enhance market presence, with a focus on positioning and campaign visuals. Our expertise includes animated content and key visuals tailored to resonate with target audiences, particularly in the Asia-Pacific region.</p>
    `,
    icon: '/icons/brand.svg',
    pattern: 'brand',
    lottie: 'https://lottie.host/6581ce62-09c7-4d3b-a783-3d34f8afc75b/yTyLXTSytT.lottie',
    show: false,
  },
  {
    id: 2,
    title: 'Brand Video',
    desc: `
      <p>We translate client visions into compelling visual narratives, ensuring alignment with business goals and audience expectations. Through close collaboration with our creative team, we deliver impactful brand videos that leave lasting impressions.</p>
    `,
    icon: '/icons/brand-video.svg',
    pattern: 'brand-video',
    lottie: 'https://lottie.host/73d285a0-8c86-4d43-9e41-bba78fad7258/qHUmfL8XmN.lottie',
    show: false,
  },
  {
    id: 3,
    title: 'Social Media Content',
    desc: `
      <p>We design content aimed at boosting online engagement and brand visibility. Using bespoke animation and illustrations, we create distinctive social media assets that drive interaction and foster audience connection.</p>
    `,
    icon: '/icons/socmed.svg',
    pattern: 'socmed',
    lottie: 'https://lottie.host/1aceb548-dce2-4577-b051-c4863a1795da/tJPn1n26Rh.lottie',
    show: false,
  },
  {
    id: 4,
    title: 'Experiential and \nInteractive Content',
    desc: `
      <p>We develop immersive experiences using animation and motion design, including Augmented Reality (AR) and Virtual Reality (VR). These interactive mediums engage audiences and deliver impactful, memorable brand experiences.</p>
    `,
    icon: '/icons/exp.svg',
    pattern: 'exp',
    lottie: 'https://lottie.host/3fe15488-e189-43a1-8f19-2e0ed1c68059/71oAG2hWdg.lottie',
    show: false,
  },
]);

const init = () => {

  $gsap.to('.graphics-container-service', {
    scrollTrigger: {
      trigger: '.trigger-service-container',
      start: isL ? '0% 60%' : '0% 40%',
      end: '100% 100%',
      scrub: 1,
    },
    y: 0,
    ease: 'power1.out'
  })

  $gsap.from('.services', {
    scrollTrigger: {
      trigger: '.trigger-service-container',
      start: isL ? '0% 60%' : '0% 80%',
      end: '100% 100%',
      scrub: 1,
    },
    opacity: 0,
    y: 150,
    ease: 'power1.out'
  })
}

let mouseX;
let mouseY;
const onSwiper = (swiper) => {
  const customCursor = document.getElementById('circle-cursor');

  const updateCursorPosition = (x, y) => {
    if (customCursor) {
      customCursor.style.transform = `translateX(${x}px) translateY(${y}px)`;
    }
  };

  swiper.on('touchStart', () => {
    const payload = {
      start: true,
      move: false,
    };
    mainStore.toggleTouchEvent(payload);
  });

  swiper.on('touchEnd', (e) => {
    updateCursorPosition(0, 0);
    const x = e.touches.currentX - e.touches.startX;
    const y = e.touches.currentY - e.touches.startY;
    customCursor.style.left = `${mouseX + x}px`;
    customCursor.style.top = `${mouseY + y}px`;
    const payload = {
      start: false,
      move: false,
    };
    mainStore.toggleTouchEvent(payload);
  });

  swiper.on('touchMove', (e) => {
    const x = e.touches.currentX - e.touches.startX;
    const y = e.touches.currentY - e.touches.startY;
    updateCursorPosition(x, y);
    const payload = {
      start: true,
      move: true,
    };
    mainStore.toggleTouchEvent(payload);
  });
};

onMounted(() => {
  document.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  });

  const to = setTimeout(() => {
    init()
    clearTimeout(to)
  }, 100)
});

onUnmounted(() => {
  document.removeEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  });
});
</script>

<style lang="scss" scoped>
.swiper-service .swiper-slide {
  width: 100%;
  height: auto !important;

  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  box-shadow: rgba(50, 50, 93, 0.1) 0px 13px 12px -5px, rgba(0, 0, 0, 0.15) 0px 8px 11px -8px;
}

.swiper.swiper-service {
  overflow: visible !important;
}
</style>